import { motion } from 'framer-motion'
import {
  Building2,
  History,
  LayoutDashboard,
  Paperclip,
  Receipt,
  Route,
  Users,
  Wallet,
} from 'lucide-react'
import { useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { TabPanel } from '@/components/ui/TabPanel'
import { cn } from '@/lib/cn'
import { TAB_MARKER_TRANSITION } from '@/lib/motion'
import { brandingRefusalFields, brandingRefusalMessage } from '@/lib/refusals'
import { useCase, useCaseTeam, useSendCaseStatus } from '@/features/cases/api/useCase'
import { useSession } from '@/features/auth/api/useSession'
import { CaseTeamSection } from '@/features/cases/components/CaseTeamSection'
import { CaseHeader } from '@/features/cases/components/CaseHeader'
import { CaseLifecycleActions } from '@/features/cases/components/CaseLifecycleActions'
import { CaseStatusSendSheet } from '@/features/cases/components/CaseStatusSendSheet'
import {
  DocumentsSection,
  FilesSection,
  HistorySection,
  LegsSection,
  MoneySection,
  OverviewSection,
  ParticipantsSection,
} from '@/features/cases/components/CaseSections'
import { ChatDock, ChatDockBadge } from '@/features/chat/components/ChatDock'
import {
  canSeeRecordChat,
  useRecordConversation,
} from '@/features/chat/api/useRecordChat'
import { useTranslation } from 'react-i18next'

/* Case detail.
 *
 * The section rail is driven by a `?section=` search param rather than a nested
 * route, so a section is linkable and survives a refresh while the sticky
 * header and the chat panel stay mounted across a switch — remounting the chat
 * every time someone checks the Legs tab would lose their scroll position and
 * their half-typed message.
 *
 * Chat is a FLOATING DOCK in the bottom-right corner on desktop and a
 * full-screen view on a phone (Story 8.3, FR-46, UX-DR-13, UX-DR-28, 0052). It
 * is not a tab: a conversation buried inside one section of one record is a
 * conversation people stop opening, which is the D1 failure the feature exists
 * to prevent. It IS dismissible now, which the desktop column it replaced was
 * not — the guarantee that carries D1's lesson moved to the pair "expanded by
 * default, and the minimised button still carries the unread count". ChatDock's
 * header comment is the record of that decision; read it before making the dock
 * start collapsed.
 *
 * Because the dock floats, it takes no width from the record: this page is one
 * column at every breakpoint, in both dock states. Both shapes are one mount —
 * see ChatDock for why that is what guarantees phone parity rather than a
 * promise of it.
 *
 * The panel itself is subject-agnostic now (0051): the same ChatDock the quote
 * detail renders, handed `kind="case"` and this record's id. It is shown only
 * to a reader the record's team actually contains, because chat is narrowed to
 * that team and every request from a panel shown to anyone else would 404.
 *
 * The page owns the PHONE view's open state rather than ChatDock, because the
 * badge that toggles it lives in the sticky header and the panel lives at the
 * end of the layout. One piece of state with two readers beats an imperative
 * handle passed between siblings. The DESKTOP dock's collapsed/expanded choice
 * is not page state at all — it is a remembered per-reader preference the dock
 * owns through `useChatDockState`, because it outlives this page. */

/* TWO SECTIONS FOR TWO KINDS OF PEOPLE, and they used to be one word over one
   of them.
 *
 * `participants` lists the parties to the SHIPMENT — shipper, consignee, agent,
 * driver — and is labelled Counterparties now. `team` lists the COLLEAGUES
 * working the case, which was a card wedged under the overview: the section
 * that decides who can see the shipment at all (AD-5, FR-56) had no place of
 * its own, while the tab called Participants answered a different question
 * entirely.
 *
 * The KEYS are unchanged on the wire: `participants` is what the table, the
 * endpoint and the DTO are called, and a rename to match a label would make the
 * screen and its contract stop being traceable to each other. */
const SECTIONS = [
  { key: 'overview', labelKey: 'cases.tabs.overview', icon: LayoutDashboard },
  { key: 'legs', labelKey: 'cases.tabs.legs', icon: Route },
  { key: 'team', labelKey: 'cases.tabs.team', icon: Users },
  { key: 'participants', labelKey: 'cases.tabs.participants', icon: Building2 },
  { key: 'money', labelKey: 'cases.tabs.money', icon: Wallet },
  /* Money then Invoices then Files: the deal, the paper we raised against it,
     and everything else that was scanned. It sits beside Money because the two
     answer consecutive questions — what is owed, and what have we actually
     invoiced for it.

     THE LABEL MOVED AND THE KEY DID NOT, and both halves are deliberate. The
     section grew a rate read-out and the payment schedule on top of the
     document table, so "Documents" stopped naming what it holds: what a person
     comes here to do is bill the client, and the table of paper is the record
     of having done it. The alternative was a second "Invoices" section beside
     this one, which would have been two tabs about one piece of paper and a
     guess on every visit about which of them to open.

     `documents` stays the key because it is the CONTRACT: `?section=documents`
     is what the invoice page's back link points at and what colleagues have
     already pasted into chat threads. The rail's labels and the URL's keys are
     allowed to disagree — the Counterparties tab has done exactly that since
     the participants rename, for the same reason. */
  { key: 'documents', labelKey: 'cases.tabs.documents', icon: Receipt },
  { key: 'files', labelKey: 'cases.tabs.files', icon: Paperclip },
  { key: 'history', labelKey: 'cases.tabs.history', icon: History },
] as const

type SectionKey = (typeof SECTIONS)[number]['key']

export function CaseDetailPage() {
  const { t } = useTranslation()
  const { caseId = '' } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const [chatOpen, setChatOpen] = useState(false)
  const [statusOpen, setStatusOpen] = useState(false)
  /* What the last send reached, held so the confirmation can NAME the address
     rather than saying "sent" and leaving the sender to trust it. Cleared when
     the sheet reopens, because a stale success above a fresh form reads as if
     this send already went. */
  const [sentTo, setSentTo] = useState<string | null>(null)

  const { data: record, isPending, isError, error, refetch } = useCase(caseId)
  const sendStatus = useSendCaseStatus(caseId)

  /* Chat narrowed to the record's TEAM (0051), so a reader who is not on this
     case would get a 404 from every chat request. Deciding it here keeps the
     panel from advertising a door that does not open — the server refuses
     regardless; this is about not offering.

     The owner id IS handed over, and passing an empty string here was a bug
     for as long as this line existed. The reasoning it replaced was that the
     owner is seeded onto `case_team` at create (migration 0007), so the team
     read would always contain them — but casefile.Service.Unassign refuses only
     the removal that would EMPTY a team, so an owner can be taken off their own
     case while a colleague remains. In that state StaffedOrOwned still serves
     them the conversation (`c.owner_id = $1 OR` the team predicate) and this
     page rendered no panel and no badge at all.

     `record` is read optionally: these hooks run before the loading and error
     branches return, because hooks cannot be called conditionally. The team
     query answers on its own and does not wait for the detail — so the owner
     id may be undefined for one render, which reads as "not yet" rather than
     as "no", exactly as an unresolved team does. */
  const { data: user } = useSession()
  const team = useCaseTeam(caseId)
  const canChat = canSeeRecordChat(
    user,
    record?.ownerId ?? '',
    team.data ?? record?.team ?? [],
  )

  /* The conversation's row, from the list the chat page already loads. It is
     where both facts the page needs come from: the id the socket subscribes to,
     and the unread count the phone badge renders. A case nobody has spoken on
     has no row, which is the ordinary state and not an error. */
  const conversation = useRecordConversation('case', caseId, canChat)
  const chatUnread = conversation?.unread ?? 0

  const requested = searchParams.get('section')
  const section: SectionKey = SECTIONS.some((s) => s.key === requested)
    ? (requested as SectionKey)
    : 'overview'

  if (isPending) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true" aria-label={t('cases.loading')}>
        <Skeleton className="h-24 w-full rounded-[var(--radius-DEFAULT)]" />
        <Skeleton className="h-10 w-full max-w-lg rounded-md" />
        <Skeleton className="h-72 w-full rounded-[var(--radius-DEFAULT)]" />
      </div>
    )
  }

  if (isError) {
    return <ErrorState error={error} onRetry={() => void refetch()} />
  }

  const filesPending = record.files.filter((file) => file.pending).length

  /* What a refused send SAYS, and the one refusal that must not be worded as a
     retry.
     `cases.statusSend.failed` — "check the connection and try again" — is the
     honest sentence for what this endpoint usually fails on: a dead network, a
     500, a shipment that moved status while the sheet was open. It is the wrong
     sentence for exactly one refusal. FR-64's branding gap comes back as a 422
     naming the ISSUING ENTITY's missing fields (internal/casefile's `fail`,
     which answers what internal/quote and internal/document already answered
     for the identical error), and retrying fixes none of it — the cure is a
     settings form nobody filled in, on a screen this page never mentions.
     Read through `lib/refusals` rather than by inspecting `problem.errors`
     here: the offer sheet, the invoice sheet and this case's own document card
     all name these fields from the same document, and a fourth reading of one
     wire shape is how two surfaces end up disagreeing about which field was
     missing. `brandingRefusalFields` answers null for everything else, which is
     what keeps the sentence above intact for every other failure — this is not
     the generic problem mapping quietly taking the message over. */
  const sendRefusal = brandingRefusalFields(sendStatus.error)

  /* Record counts on the rail, so "is anything there yet?" is answered without
     visiting the section. Files shows PENDING (attention), not total — a total
     is trivia, an unconfirmed upload is a task. */
  const counts: Partial<Record<SectionKey, number>> = {
    legs: record.legs.length,
    participants: record.participants.length,
  }

  return (
    /* ONE COLUMN, at every width. This used to be a flex ROW above lg, with the
       record on the left and a persistent chat column on the right — which cost
       the case roughly 306px of its width on every screen, whether or not the
       reader was in the conversation. The dock floats over the corner now
       (0052), so it is out of flow entirely: the two nested columns that row
       needed collapsed into this one, and `ChatDock` below is a sibling of the
       record's content only for z-order, never for layout. */
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
        <CaseHeader
          record={record}
          onSendStatus={() => {
            sendStatus.reset()
            setSentTo(null)
            setStatusOpen(true)
          }}
          /* The lifecycle surface (FR-25, FR-37), composed here rather than
             inside the header so the header keeps owning no mutation. It reads
             the same `record` the header does, so the moves it offers and the
             status pill beside them can never disagree. */
          lifecycle={<CaseLifecycleActions record={record} />}
          chatBadge={
            canChat ? (
              <ChatDockBadge onOpen={() => setChatOpen(true)} unread={chatUnread} />
            ) : null
          }
        />

        {/* The confirmation lives on the PAGE rather than inside the sheet,
            because the sheet closes on success: a message shown only there
            would be shown only to a form nobody is looking at any more. It
            names the address, so "sent" is a fact the sender can check rather
            than a reassurance. */}
        {sentTo ? (
          <div
            role="status"
            className="border-positive/30 bg-positive-soft text-positive rounded-[var(--radius-md)] border px-4 py-2.5 text-[13px]"
          >
            {t('cases.statusSend.sent', { email: sentTo })}
          </div>
        ) : null}

        {/* The secondary rail. On desktop it sits beside the content as a
            vertical list; below lg it becomes a horizontal switcher. */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
          <nav
            aria-label={t('cases.sectionsLabel')}
            className={cn(
              'asa-scroll bg-surface border-line flex shrink-0 gap-0.5 self-start',
              'overflow-x-auto rounded-[var(--radius-lg)] border p-1.5',
              'max-lg:self-stretch lg:w-48 lg:flex-col lg:overflow-visible',
            )}
          >
            {SECTIONS.map((item) => {
              const active = item.key === section
              const badge = item.key === 'files' ? filesPending : 0
              const count = counts[item.key]
              const Icon = item.icon

              return (
                <button
                  key={item.key}
                  type="button"
                  aria-current={active ? 'page' : undefined}
                  onClick={() => {
                    const next = new URLSearchParams(searchParams)
                    next.set('section', item.key)
                    setSearchParams(next, { replace: true })
                  }}
                  className={cn(
                    'group relative flex shrink-0 cursor-pointer items-center gap-2.5',
                    'rounded-[var(--radius-md)] px-3 py-2.5 text-[13px]',
                    'transition-colors duration-[var(--dur-fast)]',
                    'ease-[var(--ease-standard)] lg:w-full lg:justify-start',
                    active
                      ? 'text-text-heading font-medium'
                      : 'text-text-muted hover:bg-surface-hover hover:text-text-heading font-normal',
                  )}
                >
                  {/* The rail is a white hairline card, so the selected state
                      is a soft brand tint inside it. The brand accent stays on
                      the icon, not the label, so blue stays scarce.

                      The pill is its own element with a shared layoutId, so
                      Framer slides it between sections instead of repainting
                      one background off and another on. It works unchanged in
                      both orientations — the rail is horizontal below lg and
                      vertical above it, and the layout engine interpolates
                      whichever axis actually moved.

                      The pill is painted first and the label is lifted above it
                      with its own stacking context, rather than pushing the
                      pill behind with a negative z-index — negative would put
                      it behind the button itself and the fill would vanish. */}
                  {active ? (
                    <motion.span
                      layoutId="case-section-marker"
                      transition={TAB_MARKER_TRANSITION}
                      aria-hidden
                      className="bg-brand-soft absolute inset-0 rounded-[var(--radius-md)]"
                    />
                  ) : null}

                  <Icon
                    size={16}
                    aria-hidden
                    className={cn(
                      'relative shrink-0 transition-colors duration-[var(--dur-fast)]',
                      active ? 'text-brand' : 'text-text-faint group-hover:text-text-muted',
                    )}
                  />
                  <span className="relative">{t(item.labelKey)}</span>

                  {/* Pending files are a task and keep the attention badge;
                      plain record counts are quiet chips at the rail edge. */}
                  {badge > 0 ? (
                    <span className="bg-attention-soft text-attention asa-num relative rounded-full px-1.5 py-0.5 text-[11px] leading-none font-semibold lg:ml-auto">
                      {badge}
                    </span>
                  ) : count ? (
                    <span className="asa-num bg-surface border-line text-text-muted relative rounded-full border px-1.5 py-0.5 text-[10.5px] leading-none font-normal lg:ml-auto">
                      {count}
                    </span>
                  ) : null}
                </button>
              )
            })}
          </nav>

          <TabPanel tabKey={section} className="min-w-0 flex-1">
            {section === 'overview' ? <OverviewSection record={record} /> : null}
            {section === 'legs' ? <LegsSection record={record} /> : null}
            {/* FR-69 still puts assignment ON THE RECORD rather than on a
                settings screen — a section of the case is on the record. What
                changed is that it is no longer a card appended below the
                overview, where the control that decides who can SEE the
                shipment sat under the shipment's facts and was found by
                scrolling. */}
            {section === 'team' ? <CaseTeamSection record={record} /> : null}
            {section === 'participants' ? (
              <ParticipantsSection record={record} />
            ) : null}
            {section === 'money' ? (
              /* The whole record, like every other section: the money card
                 needs the block, the reason there is none and the case id, and
                 three props that have to agree with each other are one prop.
                 `onRetry` is the page's refetch because the block rides the
                 composed detail — the section has no query of its own to retry,
                 which is why an unavailable block used to have no way out. */
              <MoneySection record={record} onRetry={() => void refetch()} />
            ) : null}
            {section === 'documents' ? <DocumentsSection record={record} /> : null}
            {section === 'files' ? <FilesSection caseId={record.id} /> : null}
            {/* The case id, not a slice of the record: the feed reads the
                action log and the financial trail itself, which is what lets
                one dead source still show the other. */}
            {section === 'history' ? <HistorySection caseId={record.id} /> : null}
          </TabPanel>
        </div>

      {/* Sending the client their status (FR-41). The sheet stays open on
          failure holding the note, so nothing typed is lost to a refused send
          (UX-DR-18) — and closes only once the server has accepted it, because
          an email cannot be unsent and "sent" must mean sent. */}
      <CaseStatusSendSheet
        open={statusOpen}
        onClose={() => setStatusOpen(false)}
        record={record}
        pending={sendStatus.isPending}
        submitError={
          !sendStatus.isError
            ? undefined
            : sendRefusal !== null
              ? brandingRefusalMessage(sendRefusal, t)
              : t('cases.statusSend.failed')
        }
        onSubmit={(values) => {
          sendStatus.mutate(values, {
            onSuccess: (result) => {
              setSentTo(result.sentTo)
              setStatusOpen(false)
            },
          })
        }}
      />

      {/* The conversation. Fixed-positioned in both its shapes — a floating
          dock in the bottom-right corner above lg, a full-screen layer below it
          — so it takes part in no layout here and its position in this tree is
          only about z-order and about being a sibling rather than a child of
          something that scrolls. See ChatDock for why the two are one mount.

          The number and the client come off the record this page already holds
          rather than being fetched again: they are the dock's subtitle, and one
          request for two strings would be a request too many.

          `unread` is the same count the header badge renders, from the same
          conversation row — the phone badge and the desktop dock's minimised
          button must never disagree about whether somebody has said something.

          Not rendered at all for a reader who is not on this case: every
          request it made would answer 404 (0051). */}
      {canChat ? (
        <ChatDock
          kind="case"
          subjectId={record.id}
          conversationId={conversation?.id}
          subjectNumber={record.number}
          subjectClient={record.clientName}
          unread={chatUnread}
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          allowAttachments
        />
      ) : null}
    </div>
  )
}
